import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { trackEvent, trackConversion } from "@/lib/gtag";
import {
  startCheckout,
  startCreditPackPurchase,
  BillingError,
  type Plan,
  type CreditPack,
} from "@/lib/billing";

/**
 * Handles the magic-link redirect.
 *
 * Three intents the user might have stashed before clicking the link:
 *   1. postLoginRedirect — where to land them (default `/`)
 *   2. postLoginIntent.kind = "checkout" — resume subscription checkout
 *   3. postLoginIntent.kind = "credit_pack" — resume credit pack purchase
 *
 * Anything we can't resume cleanly just navigates to the redirect destination.
 */
type StashedIntent =
  | { kind: "checkout"; plan: Plan }
  | { kind: "credit_pack"; pack: CreditPack };

export function AuthCallbackPage() {
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;

    async function go() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      // Fire analytics event + Google Ads conversion on successful magic-link sign-in.
      // This counts ALL sign-ins (not just new accounts) — Google Ads dedupes by user
      // anyway, and we don't have a server-side "is_new_user" boolean to send.
      if (sessionData.session?.user) {
        trackEvent("login", { method: "magic_link" });
        trackConversion(import.meta.env.VITE_GTAG_CONV_SIGNUP);
      }

      const destination = sessionStorage.getItem("doclens.postLoginRedirect") || "/";
      const intentRaw = sessionStorage.getItem("doclens.postLoginIntent");
      sessionStorage.removeItem("doclens.postLoginRedirect");
      sessionStorage.removeItem("doclens.postLoginIntent");

      if (intentRaw) {
        try {
          const intent = JSON.parse(intentRaw) as StashedIntent;
          if (intent.kind === "checkout" && intent.plan) {
            navigate(destination, { replace: true });
            await startCheckout(intent.plan);
            return;
          }
          if (intent.kind === "credit_pack" && intent.pack) {
            navigate(destination, { replace: true });
            await startCreditPackPurchase(intent.pack);
            return;
          }
        } catch (err) {
          if (!(err instanceof BillingError)) {
            console.warn("[auth/callback] could not resume intent", err);
          }
        }
      }

      navigate(destination, { replace: true });
    }

    go();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center text-neutral-500 text-sm">
      Signing you in…
    </div>
  );
}
